import useSWR from 'swr';
import { useAuth } from '@clerk/nextjs';
import api from '@/service/api';

/**
 * Get customer billing information from backend
 * Replaces Autumn's useCustomer hook with backend API call
 */
export function useCustomer() {
  const { getToken, isSignedIn } = useAuth();

  const { data, error, isLoading, mutate } = useSWR(
    isSignedIn ? '/billing/info' : null,
    async () => {
      const token = await getToken();
      const response = await api.billing.getBillingInfo(token);
      return response.data;
    },
    {
      dedupingInterval: 5000, // Deduplicate identical requests within 5s window
      revalidateOnFocus: false, // Don't refetch on tab switch (saves ~750ms per switch)
      refreshInterval: 120000, // Refresh every 2 minutes (billing data doesn't change per-second)
    }
  );

  // Transform backend response — include all feature balances
  const customer = data ? {
    customer_id: data.customer_id,
    product_id: data.current_plan,
    has_active_subscription: data.subscription_active,
    features: {
      credits: {
        balance: data.credits_balance ?? 0,
        included_usage: data.credits_limit ?? 0,
      },
    },
    // All feature balances from Autumn (credits, clip_exports)
    feature_balances: data.feature_balances ?? {},
    // Scheduled plan change (downgrade pending end of billing period)
    scheduled_plan: data.scheduled_plan ?? null,
    // Unix seconds when the allowance resets; the backend reads it off the subscription.
    current_period_end: data.current_period_end ?? null,
  } : null;

  return {
    customer,
    isLoading,
    error,
    mutate, // Allow manual refresh
  };
}
